/**
 * Admin User control module
 */
import { API_URL, requestData } from "./utils";
import User, { AdminUser, EnterpriseUser, UserTypes } from "../../../dto/User";
import { ServerGetResponse } from "../../../dto/ServerResponse";
import { ServerGetPayload } from "../../../dto/ServerRequest";

export interface CreateUserRequest {
  name_title: string;
  first_names: string;
  last_name: string;
  title: string;
  phone_number: string;
  email: string;
  lang?: string;
}
const unknown_type_msg = "unknown type";
const LOGIN_TYPES: {
  admin: UserTypes;
  enterprise: UserTypes;
} = {
  admin: "admin",
  enterprise: "enterprise",
};
Object.freeze(LOGIN_TYPES);

const bytesToBase64 = (bytes : Uint8Array) => {
  const binString = Array.from(bytes, (byte) =>
    String.fromCodePoint(byte),
  ).join("");
  return btoa(binString);
}

export default class UserModule {
  activateUser(
    user_id: string,
    type: UserTypes,
    enterprise_id: string,
    entity_id: string,
  ) {
    const login_type = LOGIN_TYPES[type];
    if (!login_type) {
      return Promise.reject(unknown_type_msg);
    }
    return requestData(
      "POST",
      `/user/activate/${user_id}`,
      {
        enterprise_id,
        entity_id,
      },
      {
        "x-login-context": login_type,
      },
    );
  }
  createUser(params: CreateUserRequest, type: UserTypes, lang: string) {
    const data: CreateUserRequest = {
      name_title: params.name_title,
      first_names: params.first_names,
      last_name: params.last_name,
      title: params.title,
      phone_number: params.phone_number,
      email: params.email,
      lang,
    };
    const login_type = LOGIN_TYPES[type];
    if (!login_type) {
      return Promise.reject(unknown_type_msg);
    }
    return requestData<User | EnterpriseUser, CreateUserRequest>(
      "POST",
      `/user`,
      data,
      {
        "x-login-context": login_type,
      },
    );
  }
  resetPassword(username: string, type: UserTypes, lang: string) {
    const login_type = LOGIN_TYPES[type];
    if (!login_type) {
      return Promise.reject(unknown_type_msg);
    }
    return requestData(
      "POST",
      `/user/reset_password`,
      {
        username,
        lang,
      },
      {
        "x-login-context": login_type,
      },
    );
  }
  updatePassword(
    user_id: string,
    new_password: string,
    type: UserTypes,
    enterprise_id: string,
    entity_id: string,
    expire: string,
    token: string,
    admin_user_status: string
  ) {
    const login_type = LOGIN_TYPES[type];
    if (!login_type) {
      return Promise.reject(unknown_type_msg);
    }

    if (login_type === "admin") {
      return requestData("POST", `/admin_user/update_password/${user_id}`, {
        enterprise_id,
        entity_id,
        new_password,
        expire,
        token,
        admin_user_status
      });
    } else {
      return requestData(
        "POST",
        `/enterprise_user_update_password/${enterprise_id}/${user_id}`,
        {
          enterprise_id,
          entity_id,
          new_password,
          expire,
          token,
        },
      );
    }
  }
  loginUser(email: string, password: string, type: UserTypes) {
    const login_type = LOGIN_TYPES[type];
    if (!login_type) {
      return Promise.reject(unknown_type_msg);
    }
    return requestData("GET", `/user/login`, null, {
      Authorization: `Basic ${bytesToBase64(new TextEncoder().encode(email + ":" + password))}`,
      "x-login-context": login_type,
    });
  }
  samlLogin(samlToken: string, type: UserTypes) {
    const login_type = LOGIN_TYPES[type];
    if (!login_type) {
      return Promise.reject(unknown_type_msg);
    }
    return requestData("GET", `/auth/sso/saml/intra-login`, null, {
      "x-login-context": login_type,
      "x-saml-token": samlToken,
    });
  }
  getMfaProfile(email: string, type: UserTypes) {
    const login_type = LOGIN_TYPES[type];
    if (!login_type) {
      return Promise.reject(unknown_type_msg);
    }
    return requestData(
      "GET",
      `/auth/user-mfa-profile`,
      {
        email,
      },
      {
        "x-login-context": login_type,
      },
    );
  }
  refetchToken() {
    return requestData("GET", `/user/token-refresh`, null);
  }
  logoutUser(type: UserTypes) {
    const login_type = LOGIN_TYPES[type];
    return requestData("DELETE", `/user/logout`, null, {
      "x-login-context": login_type,
    });
  }
  getUserDetails(
    user_id: string,
    type: UserTypes,
    enterprise_id: string,
    entity_id: string,
  ) {
    const login_type = LOGIN_TYPES[type];
    if (!login_type) {
      return Promise.reject(unknown_type_msg);
    } else if (login_type === "admin") {
      return requestData<User>("GET", `/user/${user_id}`, null, {
        "x-login-context": login_type,
      });
    } else if (login_type === "enterprise") {
      if (!entity_id) {
        return requestData<EnterpriseUser>(
          "GET",
          `/enterprise_user/${enterprise_id}/${user_id}`,
          null,
          {
            "x-login-context": login_type,
          },
        );
      } else {
        return requestData<EnterpriseUser>(
          "GET",
          `/enterprise_user/${enterprise_id}/${entity_id}/${user_id}`,
          null,
          {
            "x-login-context": login_type,
          },
        );
      }
    }
  }
  getPasswordPolicy() {
    return requestData("GET", `/sys/password_policy`);
  }
  getQuery() {
    return requestData("GET", `/query/admin_user`);
  }
  requestQueryLink(
    query: ServerGetPayload,
    link: string = "/query/admin_user",
    abortSignal?: AbortSignal,
  ) {
    return requestData<
      ServerGetResponse<{
        admin_users: AdminUser[];
        total_found: number;
      }>,
      ServerGetPayload
    >("GET", link, query, undefined, abortSignal);
  }

  createAdmin(user: AdminUser) {
    return requestData<{}, AdminUser>("POST", `/user`, user);
  }

  update(user: AdminUser) {
    return requestData<{}, AdminUser>("PUT", `/admin_user/${user.user_id}`, user);
  }

  // methods for changing the status of user in user management section of  user activation sidebar
  suspend(user_id: string, reason: string | null = null) {
    return requestData<
      {},
      {
        reason: string | null;
      }
    >("POST", `/user/suspend/${user_id}`, { reason });
  }
  unsuspend(user_id: string) {
    return requestData<{}>("POST", `/user/unsuspend/${user_id}`);
  }
  retire(user_id: string, reason: string | null = null) {
    return requestData<
      {},
      {
        reason: string | null;
      }
    >("POST", `/user/retire/${user_id}`, { reason });
  }
  activate_user(user_id: string) {
    return requestData<{}>("POST", `/user/activate/${user_id}`);
  }

  resendActivationMail(user){
    return requestData<{}, AdminUser>("POST", `/user/resend_activation_mail`, user);
  }

  // methods for changing the status of enterprise users in enterprise users section of user activation  sidebar
  activate_enterprise_user(enterprise_id, user_id, entity_id = null) {
    return requestData(
      "POST",
      `/enterprise_user_activate/${enterprise_id}${
        entity_id ? `/${entity_id}` : ""
      }/${user_id}`,
    );
  }

  retire_enterprise_user(enterprise_id, user_id, entity_id = null) {
    return requestData(
      "POST",
      `/enterprise_user_retire/${enterprise_id}${
        entity_id ? `/${entity_id}` : ""
      }/${user_id}`,
    );
  }

  suspend_enterprise_user(enterprise_id, user_id, entity_id = null) {
    return requestData(
      "POST",
      `/enterprise_user_suspend/${enterprise_id}${
        entity_id ? `/${entity_id}` : ""
      }/${user_id}`,
    );
  }

  unsuspend_enterprise_user(enterprise_id, user_id, entity_id = null) {
    return requestData(
      "POST",
      `/enterprise_user_unsuspend/${enterprise_id}${
        entity_id ? `/${entity_id}` : ""
      }/${user_id}`,
    );
  }

  // methods for re sending the activation link

  userResendLink(
    enterprise_id,
    user_id,
    entity_id = null,
    primary_language = "JAPANESE",
  ) {
    const path = entity_id
      ? `/enterprise_user_resend_activation/${enterprise_id}/${entity_id}`
      : `/enterprise_user_resend_activation/${enterprise_id}`;
    return requestData("POST", path, { user_id, primary_language });
  }

  userResendInvitationLink(
    enterprise_id,
    user_invitation_id,
    entity_id = null,
    primary_language = "JAPANESE",
  ) {
    const path = entity_id
      ? `/enterprise_user_invitation_resend/${enterprise_id}/${entity_id}`
      : `/enterprise_user_invitation_resend/${enterprise_id}`;
    return requestData("POST", path, { user_invitation_id, primary_language });
  }

  getEnterpriseUsers(query: Object) {
    return requestData("GET", `/query/users`, {
      page: query["page"],
      "page-size": query["page-size"],
      "sort-key": query["sort-key"],
      "sort-order": query["sort-order"],
      "enterprise-id": query["enterprise_id"],
      "reseller-id": query["reseller-id"],
    });
  }
  
  getEnterpriseUsersCsvURL(query, selectedEnterprise, link: string = `/download/csv/users/${selectedEnterprise}`) {
    return API_URL + link + "?" + new URLSearchParams(query);
  }
  
  fetchResellerAdminUsers(query: Object) {
    return requestData("GET", `/query/admin_user/reseller`, {
      page: query["page"],
      "page-size": query["page-size"],
      "sort-key": query["sort-key"],
      "sort-order": query["sort-order"],
      "reseller-id": query["reseller-id"],
    });
  }
}
